// OPEN BIDS page: reveal per-row actions for the connected wallet and wire
// SELL (into a bid's best fill) / CANCEL (own bid, whole) / REFRESH, plus the
// top-offers sell strip for the connected holder. Rows are one per (bid,
// component) — per monke for list components — at the bid's best live fill,
// with how many fills the bid has left.
import { api, external, flows } from "./trade.js";
import { currentAccount, ensureConnected } from "./connect.js";
import { toast, confirmWithFee, pickModal } from "./ui.js";
import { btcShort } from "./collection-bid.js";
import type { AttributeFilter } from "../client/api.js";
import { pendingChanges, recordChange, type PlacedBid } from "./local-changes.js";
import { initBidManager } from "./bid-manager.js";
import { t } from "../client/i18n.js";
import { markStink } from "../client/stink.js";

const trunc = (s: string, n: number) => (s.length > n ? `${s.slice(0, n)}...` : s);
// The server rows' address form (fmt.shortAddr with 4).
const shortAddr = (a: string) => (a.length <= 9 ? a : `${a.slice(0, 4)}…${a.slice(-4)}`);
const spriteCol = (id: number) => (id - 1) % 100;
const spriteRow = (id: number) => Math.floor((id - 1) / 100);
const scopeLabel = (f: AttributeFilter | null) => {
  const monkes = (f as { monkes?: number[] } | null)?.monkes;
  if (Array.isArray(monkes)) return monkes.map((n) => `#${n}`).join(", ");
  return f && Object.keys(f).length
    ? Object.entries(f)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ")
    : t("client.bids.any_monke");
};

// Per-row action visibility (CANCEL / SELL) is applied at parse time by the
// inline script under the table in bids.html — this module only wires the
// click handlers.

let busy = false;
const run = async (btn: HTMLButtonElement, fn: () => Promise<void>) => {
  if (busy || manager.confirmInFlight()) return;
  busy = true;
  const label = btn.textContent;
  btn.textContent = "…";
  btn.disabled = true;
  try {
    await fn();
  } catch (err) {
    toast(String((err as Error)?.message ?? err), "error");
  } finally {
    btn.textContent = label;
    btn.disabled = false;
    busy = false;
  }
};

document.addEventListener("click", (e) => {
  const btn = (e.target as Element).closest<HTMLButtonElement>(
    "[data-sell], [data-cancel], [data-refresh]",
  );
  if (!btn) return;
  const row = btn.closest<HTMLTableRowElement>("tr[data-bid]")!;
  const bidId = row.dataset.bid!;
  const componentIndex = Number(row.dataset.component ?? 0);
  const offerId = row.dataset.offer!;

  if (btn.hasAttribute("data-refresh")) {
    // Re-fund the row's offer under a fresh throwaway key (nothing is
    // persisted): wallet signs a new funding psbt, items re-sign locally.
    void run(btn, async () => {
      const account = await ensureConnected();
      if (!account) return;
      const refreshed = await flows.refreshCollectionOffer(account, offerId, (p) => {
        btn.textContent = p.toUpperCase();
      });
      toast(t("client.bids.bid_refreshed", { n: refreshed }), "ok");
      markFresh(offerId);
      recordChange({ t: "coll-refreshed", id: offerId });
    });
  } else if (btn.hasAttribute("data-sell")) {
    void run(btn, async () => {
      // The fill a seller gets is the bid's highest-priced live slot — ask
      // the server which offer that is right now (the row may be stale).
      const best = await api.bidsApi.best(bidId, componentIndex);
      const sold = await sellInto(best.offerId, best.priceSats);
      if (sold) {
        consumeFill(bidId, best.offerId);
        recordChange({ t: "slot-filled", bidId, offerId: best.offerId });
        void renderSellStrip();
      }
    });
  } else {
    void run(btn, async () => {
      await flows.cancelBids(row.dataset.bidder!, { bidIds: [bidId] });
      toast(t("client.bids.bid_cancelled"), "ok");
      removeBidRows(bidId);
      recordChange({ t: "bid-cancelled", bidId });
      void manager.reload();
    });
  }
});

/** Remove a row in place and keep the header count honest. */
function removeRow(row: HTMLTableRowElement): void {
  row.remove();
  bumpBidsCount(-1);
}

/** Every row of a bid — each component, per monke for lists. */
function removeBidRows(bidId: string): void {
  for (const row of document.querySelectorAll<HTMLTableRowElement>(`tr[data-bid="${bidId}"]`)) {
    removeRow(row);
  }
}

function removeComponentRows(bidId: string, componentIndex: number): void {
  for (const row of document.querySelectorAll<HTMLTableRowElement>(
    `tr[data-bid="${bidId}"][data-component="${componentIndex}"]`,
  )) {
    removeRow(row);
  }
}

/** One fill of a bid was taken: every row of the bid has one fewer left
 * (its best offer changed — the next SELL asks the server again); rows of
 * a bid with nothing left go. */
function consumeFill(bidId: string, offerId: string): void {
  for (const row of document.querySelectorAll<HTMLTableRowElement>(`tr[data-bid="${bidId}"]`)) {
    const left = Number(row.dataset.left ?? 1) - 1;
    if (left <= 0) {
      removeRow(row);
      continue;
    }
    row.dataset.left = String(left);
    if (row.dataset.offer === offerId) delete row.dataset.offer;
    paintLeft(row, left);
  }
}

function markFresh(offerId: string): void {
  for (const r of document.querySelectorAll<HTMLTableRowElement>(`tr[data-offer="${offerId}"]`)) {
    r.removeAttribute("data-stale");
    const b = r.querySelector<HTMLElement>("[data-refresh]");
    if (b) b.hidden = true;
  }
}

function paintLeft(row: HTMLTableRowElement, left: number): void {
  let tag = row.querySelector<HTMLElement>(".left");
  if (left <= 1) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement("span");
    tag.className = "dim left";
    row.querySelector("td.num")?.appendChild(tag);
  }
  tag.textContent = t("client.bids.left", { n: left });
}

function bumpBidsCount(delta: number): void {
  const el = document.getElementById("bids-count");
  const n = Number(el?.textContent);
  if (el && Number.isFinite(n)) el.textContent = String(Math.max(0, n + delta));
}

/** Insert a freshly placed bid's rows (own bid: CANCEL visible, REFRESH
 * hidden — nothing is stale yet). */
function insertBidRows(placed: PlacedBid): void {
  const tbody = document.querySelector("#bids-all tbody");
  if (!tbody) return;
  for (const c of [...placed.components].reverse()) {
    if (document.querySelector(`tr[data-bid="${placed.id}"][data-component="${c.componentIndex}"]`)) continue;
    const monkes = (c.attributeFilter as { monkes?: number[] } | null)?.monkes;
    // A list component is one row PER monke — the table never shows that
    // they share a funding (mirror of the server render).
    const kind = Array.isArray(monkes)
      ? "individual"
      : c.attributeFilter && Object.keys(c.attributeFilter).length
        ? "trait"
        : "collection";
    const rows = Array.isArray(monkes) ? monkes.map((m) => ({ monkeId: m })) : [{ monkeId: null }];
    for (const { monkeId } of [...rows].reverse()) {
      const tr = document.createElement("tr");
      tr.dataset.bid = placed.id;
      tr.dataset.component = String(c.componentIndex);
      tr.dataset.offer = c.offerId;
      tr.dataset.kind = kind;
      tr.dataset.bidder = placed.bidder;
      tr.dataset.priceSats = String(c.priceSats);
      tr.dataset.left = String(c.left);
      tr.innerHTML =
        `<td></td><td class="num"><span class="price-btc"></span></td>` +
        `<td class="buyer"><a></a></td><td class="expiry"><span class="dim">${t("client.bids.open")}</span></td>` +
        `<td><button type="button" class="btn btn-ghost" data-cancel>${t("client.bids.cancel")}</button>` +
        `<button type="button" class="btn btn-ghost" hidden data-refresh>${t("client.bids.refresh")}</button>` +
        `<button type="button" class="btn" hidden data-sell>${t("client.bids.sell")}</button></td>`;
      const scope = tr.children[0] as HTMLElement;
      if (monkeId !== null) {
        const link = document.createElement("a");
        link.href = `/${monkeId}`;
        link.textContent = `#${monkeId}`;
        scope.appendChild(link);
      } else {
        scope.textContent = scopeLabel(c.attributeFilter as AttributeFilter | null);
        scope.title = scope.textContent;
      }
      tr.querySelector(".price-btc")!.textContent = btcShort(c.priceSats);
      paintLeft(tr, c.left);
      const a = tr.querySelector(".buyer a") as HTMLAnchorElement;
      a.href = `/account/${placed.bidder}`;
      a.title = placed.bidder;
      a.textContent = shortAddr(placed.bidder);
      markStink(a, placed.bidder);
      tbody.prepend(tr);
      bumpBidsCount(1);
    }
  }
}

const manager = initBidManager({
  isPageBusy: () => busy,
  insertMarketRows: (bid) => insertBidRows(bid),
  removeMarketBid: (bidId) => removeBidRows(bidId),
  removeMarketComponent: (bidId, componentIndex) => removeComponentRows(bidId, componentIndex),
});

// Layer recent local changes over this (possibly SWR-cached) render — every
// applier is idempotent, so a fresh render that already includes a change is
// left untouched.
for (const c of pendingChanges()) {
  if (c.t === "bid-cancelled") {
    removeBidRows(c.bidId);
  } else if (c.t === "component-cancelled") {
    removeComponentRows(c.bidId, c.componentIndex);
  } else if (c.t === "slot-filled") {
    if (document.querySelector(`tr[data-offer="${c.offerId}"]`)) consumeFill(c.bidId, c.offerId);
  } else if (c.t === "coll-refreshed") {
    markFresh(c.id);
  } else if (c.t === "coll-cancelled") {
    for (const row of document.querySelectorAll<HTMLTableRowElement>(`tr[data-offer="${c.id}"]`)) removeRow(row);
  } else if (c.t === "bid-placed") {
    insertBidRows(c.bid);
  }
}

/** Sell one of the holder's qualifying monkes into an offer: pick WHICH
 * monke when several qualify (sprite grid), then fee-confirm. */
async function sellInto(offerId: string, priceSats: number): Promise<boolean> {
  const account = await ensureConnected();
  if (!account) return false;
  const items = await flows.acceptableCollectionItems(offerId, account.ordinalsAddress);
  if (items.length === 0) {
    throw new Error(t("client.bids.no_matching"));
  }

  const monkeLabel = (i: (typeof items)[number]) =>
    i.monkeId != null ? `#${i.monkeId}` : trunc(i.inscriptionId, 12);
  let chosen = items[0]!;
  if (items.length > 1) {
    const picked = await pickModal(
      t("client.bids.sell_which", { n: items.length }),
      items.map((i) => ({
        value: i.inscriptionId,
        label: monkeLabel(i),
        col: i.monkeId != null ? spriteCol(i.monkeId) : undefined,
        row: i.monkeId != null ? spriteRow(i.monkeId) : undefined,
      })),
    );
    if (picked == null) return false;
    chosen = items.find((i) => i.inscriptionId === picked)!;
  }
  const rate = await confirmWithFee(
    t("client.bids.sell_into_bid"),
    [
      [t("client.bids.monke"), monkeLabel(chosen)],
      [t("client.bids.sell_for"), btcShort(priceSats)],
    ],
    t("client.bids.sell"),
    external.getFeeTiers(),
    { feeFor: flows.quoteSellCollection(offerId, chosen.inscriptionId), usd: external.getBtcUsd() },
  );
  if (rate == null) return false;
  const txs = await flows.acceptCollectionItem(account, offerId, chosen.inscriptionId, rate);
  toast(t("client.bids.sold", { txs: txs.map((x) => x.name).join(" + ") }), "ok");
  recordChange({
    t: "sale-pending",
    inscriptionId: chosen.inscriptionId,
    monkeId: chosen.monkeId,
  });
  return true;
}

/** Top-of-page strip: for each collection/trait scope, the best open offer the
 * connected holder's monkes qualify for. The container's height is reserved at
 * parse time for wallet users (bids.html CSS keys on html[data-wallet]), so
 * filling it never shifts the page. */
async function renderSellStrip(): Promise<void> {
  const host = document.getElementById("sell-strip");
  const account = currentAccount();
  if (!host || !account) return;
  try {
    const rows = (await api.collectionApi.sellable(account.ordinalsAddress)).filter(
      // Only bids a held, unmoved monke can accept right now (sellable also
      // lists offers whose held matches moved since the last refresh), and
      // never your own — selling into it is a no-op.
      (r) => r.acceptable > 0 && r.bidder !== account.ordinalsAddress,
    );
    const best = new Map<string, { id: string; priceSats: number; acceptable: number }>();
    for (const r of rows) {
      const key = scopeLabel(r.attributeFilter);
      const cur = best.get(key);
      if (!cur || r.priceSats > cur.priceSats) {
        best.set(key, { id: r.id, priceSats: r.priceSats, acceptable: r.acceptable });
      }
    }
    const title = document.createElement("div");
    title.className = "dim";
    title.textContent = t("client.bids.top_offers");
    host.replaceChildren(title);
    if (best.size === 0) {
      const none = document.createElement("div");
      none.className = "dim";
      none.textContent = t("client.bids.none_right_now");
      host.appendChild(none);
      return;
    }
    const strip = document.createElement("div");
    strip.className = "sell-strip-row";
    for (const [label, offer] of [...best.entries()].sort(
      (a, b) => b[1].priceSats - a[1].priceSats,
    )) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "btn btn-ghost";
      b.textContent = t("client.bids.strip_sell", { label, price: btcShort(offer.priceSats) });
      b.title = t("client.bids.qualify_title", { n: offer.acceptable });
      b.onclick = () =>
        void run(b, async () => {
          const sold = await sellInto(offer.id, offer.priceSats);
          if (sold) {
            // That fill is consumed — update the table row it backed and
            // re-derive the strip from the API's updated state.
            const row = document.querySelector<HTMLTableRowElement>(`tr[data-offer="${offer.id}"]`);
            if (row?.dataset.bid) {
              consumeFill(row.dataset.bid, offer.id);
              recordChange({ t: "slot-filled", bidId: row.dataset.bid, offerId: offer.id });
            }
            void renderSellStrip();
          }
        });
      strip.appendChild(b);
    }
    host.appendChild(strip);
  } catch {
    /* strip is best-effort — the table's SELL buttons still work */
  }
}
void renderSellStrip();

/** Relative time for a refresh stamp (the server formats its own rows). */
function agoText(iso: string): string {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 3600) return t("client.bids.ago_minutes", { n: Math.floor(s / 60) });
  if (s < 86400) return t("client.bids.ago_hours", { n: Math.floor(s / 3600) });
  return t("client.bids.ago_days", { n: Math.floor(s / 86400) });
}

function tileFor(monkeId: number, priceSats: number): HTMLElement {
  const cell = document.createElement("div");
  cell.className = "monke-cell";
  const a = document.createElement("a");
  a.className = "monke-tile";
  a.href = `/${monkeId}`;
  a.title = `#${monkeId}`;
  const sprite = document.createElement("span");
  sprite.className = "msprite";
  sprite.style.setProperty("--col", String(spriteCol(monkeId)));
  sprite.style.setProperty("--row", String(spriteRow(monkeId)));
  sprite.setAttribute("role", "img");
  sprite.setAttribute("aria-label", `#${monkeId}`);
  a.appendChild(sprite);
  cell.appendChild(a);
  const cap = document.createElement("div");
  cap.className = "tile-caption";
  cap.textContent = t("client.bids.tile_bid", { price: btcShort(priceSats) });
  cell.appendChild(cap);
  return cell;
}

/** "Bids you qualify for": EVERY bid the connected wallet can sell into,
 * from one sellable call (not just this page's rows), best price first —
 * individual bids as tiles too; every row gets SELL.
 * The page list below drops those and notes a bid a held monke would
 * qualify for had it not moved since the bid's last refresh. Nothing
 * happens without a wallet. */
async function renderQualify(): Promise<void> {
  const account = currentAccount();
  const section = document.getElementById("bids-qualify");
  const grid = document.getElementById("qualify-grid");
  const tbody = section?.querySelector("tbody");
  if (!account || !section || !grid || !tbody) return;
  let rows: Awaited<ReturnType<typeof api.collectionApi.sellable>>;
  try {
    rows = await api.collectionApi.sellable(account.ordinalsAddress);
  } catch {
    return; // the page stays as rendered; SELL stays hidden
  }
  const me = account.ordinalsAddress;
  const key = (bidId: string, component: number) => `${bidId}:${component}`;
  const accepted = rows
    .filter((r) => r.acceptable > 0 && r.bidder !== me)
    .sort((a, b) => b.priceSats - a.priceSats);
  const acceptedKeys = new Set(accepted.map((r) => key(r.bidId, r.componentIndex)));
  const movedOnly = new Map(
    rows.filter((r) => r.acceptable === 0 && r.moved > 0).map((r) => [key(r.bidId, r.componentIndex), r.moved]),
  );

  for (const row of document.querySelectorAll<HTMLTableRowElement>("#bids-all tr[data-bid]")) {
    if (row.dataset.bidder === me) continue;
    const k = key(row.dataset.bid!, Number(row.dataset.component ?? 0));
    if (acceptedKeys.has(k)) {
      row.remove(); // shown in the section above
      continue;
    }
    const moved = movedOnly.get(k);
    if (moved) {
      const note = document.createElement("div");
      note.className = "moved-note";
      note.textContent = t("client.bids.moved_note", { n: moved });
      row.children[0]?.appendChild(note);
    }
  }

  tbody.replaceChildren();
  grid.replaceChildren();
  for (const r of accepted) {
    const monkes = (r.attributeFilter as { monkes?: number[] } | null)?.monkes;
    const individual = Array.isArray(monkes);
    const kind = individual ? "individual" : r.attributeFilter && Object.keys(r.attributeFilter).length ? "trait" : "collection";
    // A list bid: only the listed monkes this wallet holds and can sell.
    const targets = individual
      ? monkes.filter((m) => r.acceptableMonkeIds.includes(m)).map((m) => ({ monkeId: m as number | null }))
      : [{ monkeId: null }];
    for (const { monkeId } of targets) {
      const tr = document.createElement("tr");
      tr.dataset.bid = r.bidId;
      tr.dataset.component = String(r.componentIndex);
      tr.dataset.offer = r.id;
      tr.dataset.kind = kind;
      tr.dataset.bidder = r.bidder;
      tr.dataset.priceSats = String(r.priceSats);
      tr.dataset.left = String(r.left);
      tr.innerHTML =
        `<td></td><td class="num"><span class="price-btc"></span></td>` +
        `<td class="buyer"><a></a></td><td class="expiry"><span class="dim">${t("client.bids.open")}</span></td><td></td>`;
      const scope = tr.children[0] as HTMLElement;
      if (monkeId !== null) {
        const link = document.createElement("a");
        link.href = `/${monkeId}`;
        link.textContent = `#${monkeId}`;
        scope.appendChild(link);
      } else {
        const span = document.createElement("span");
        span.textContent = scopeLabel(r.attributeFilter);
        span.title = span.textContent;
        scope.appendChild(span);
      }
      const refreshed = document.createElement("div");
      refreshed.className = "refreshed";
      refreshed.title = new Date(r.refreshedAt).toLocaleDateString(document.documentElement.lang || "en", { year: "numeric", month: "short", day: "numeric" });
      refreshed.textContent = t("client.bids.refreshed_ago", { ago: agoText(r.refreshedAt) });
      scope.appendChild(refreshed);
      tr.querySelector(".price-btc")!.textContent = btcShort(r.priceSats);
      paintLeft(tr, r.left);
      const a = tr.querySelector(".buyer a") as HTMLAnchorElement;
      a.href = `/account/${r.bidder}`;
      a.title = r.bidder;
      a.textContent = shortAddr(r.bidder);
      markStink(a, r.bidder);
      // SELL on every qualifying row — an individual bid sells the listed
      // monke the same way (its number links to the item page).
      const sell = document.createElement("button");
      sell.type = "button";
      sell.className = "btn";
      sell.dataset.sell = "";
      sell.textContent = t("client.bids.sell");
      (tr.children[4] as HTMLElement).appendChild(sell);
      if (monkeId !== null) grid.appendChild(tileFor(monkeId, r.priceSats));
      tbody.appendChild(tr);
    }
  }
  grid.hidden = grid.children.length === 0;
  section.hidden = tbody.children.length === 0;
  const title = document.getElementById("bids-other-title");
  if (title) title.textContent = t("client.bids.h_dont_qualify");
}

void renderQualify();
